const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  "AIzaSyDmgb8N3_YrAgIY7l_92sDGBwArR1dYDGo";
const GEOCODING_TIMEOUT_MS = 5000;

export const LOCATION_STORAGE_KEY = "user_location";
export const OPEN_DELIVERY_LOCATION_EVENT = "open-delivery-location";

export const openDeliveryLocation = (options) => {
  window.dispatchEvent(new CustomEvent(OPEN_DELIVERY_LOCATION_EVENT, {
    detail: { onSelect: typeof options?.onSelect === "function" ? options.onSelect : null },
  }));
};

export const GUEST_LOCATION_PRESETS = [
  {
    id: "guest-default-location-india",
    key: "india",
    city: "Bhopal",
    country: "India",
    countryCode: "IN",
    country_id: 1,
    countryId: 1,
    merchant_id: 1,
    address: "Bhopal, India",
    display_address: "Bhopal, India",
    full_address:
      "Habibganj railway station, Habibganj Pedestrian overpass, Zone-II, Habib Ganj, Bhopal, Madhya Pradesh 462023, India",
    latitude: "23.221822200000005",
    longitude: "77.43919729999998",
    selected_from_address_list: true,
    is_guest_default_location: true,
  },
  {
    id: "guest-default-location-dubai",
    key: "dubai",
    city: "Dubai",
    country: "United Arab Emirates",
    countryCode: "AE",
    country_id: 2,
    countryId: 2,
    merchant_id: 1,
    address: "Dubai, United Arab Emirates",
    display_address: "Dubai, United Arab Emirates",
    full_address: "Dubai, United Arab Emirates",
    latitude: "25.2048493",
    longitude: "55.2707828",
    selected_from_address_list: true,
    is_guest_default_location: true,
  },
];

export const DEFAULT_GUEST_LOCATION =
  GUEST_LOCATION_PRESETS.find((location) => location.countryCode === "AE") ||
  GUEST_LOCATION_PRESETS[0];

export const DEFAULT_DELIVERY_LOCATION = DEFAULT_GUEST_LOCATION;

export const MARKET_METADATA = {
  AE: {
    countryCode: "AE",
    country: "United Arab Emirates",
    currency: "AED",
    currencyLabel: "AED",
    marketLabel: "UAE",
  },
  IN: {
    countryCode: "IN",
    country: "India",
    currency: "INR",
    currencyLabel: "INR",
    marketLabel: "India",
  },
};

export const isGuestDefaultLocation = (location) =>
  Boolean(
    location?.is_guest_default_location ||
      String(location?.id || "").startsWith("guest-default-location")
  );

export const hasSelectedDeliveryLocation = (location) => {
  const lat = location?.latitude ?? location?.lat;
  const lng = location?.longitude ?? location?.lng;
  return Boolean(
    !isGuestDefaultLocation(location) &&
    String(location?.address || location?.display_address || "").trim() &&
    lat !== null && lat !== undefined && lat !== "" &&
    lng !== null && lng !== undefined && lng !== "" &&
    Number.isFinite(Number(lat)) && Math.abs(Number(lat)) <= 90 &&
    Number.isFinite(Number(lng)) && Math.abs(Number(lng)) <= 180 &&
    !(Number(lat) === 0 && Number(lng) === 0)
  );
};

const normalizeCountryCode = (countryCode) =>
  String(countryCode || "").trim().toUpperCase();

const normalizeCountryName = (countryName) =>
  String(countryName || "").trim().toLowerCase();

const hasLocationValue = (value) =>
  value !== null && value !== undefined && value !== "" && Number(value) !== 0;

const getFirstLocationValue = (...values) =>
  values.find((value) => hasLocationValue(value));

const getMarketCountryCodeById = (countryId) => {
  if (!hasLocationValue(countryId)) return "";

  return (
    GUEST_LOCATION_PRESETS.find((item) =>
      [item.country_id, item.countryId].some(
        (itemCountryId) => String(itemCountryId) === String(countryId)
      )
    )?.countryCode || ""
  );
};

const getCountryCodeFromLocationText = (location = {}) => {
  const locationText = [
    location.country,
    location.country_name,
    location.countryName,
    location.address,
    location.display_address,
    location.full_address,
    location.formatted_address,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (!locationText) return "";
  if (/\b(india|bharat)\b/.test(locationText)) return "IN";
  if (/\b(united arab emirates|uae|u\.a\.e|dubai|abu dhabi|sharjah|ajman)\b/.test(locationText)) return "AE";

  return "";
};

export const inferCountryCodeFromLatLng = (lat, lng) => {
  const latitude = Number(lat);
  const longitude = Number(lng);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return "";
  }

  if (
    latitude >= 22 &&
    latitude <= 27 &&
    longitude >= 51 &&
    longitude <= 57
  ) {
    return "AE";
  }

  if (
    latitude >= 6 &&
    latitude <= 37 &&
    longitude >= 68 &&
    longitude <= 98
  ) {
    return "IN";
  }

  return "";
};

const cloneLocation = (location) => ({
  ...location,
  address: location.display_address || location.address,
});

export const getGuestLocationDisplayText = (
  location,
  fallback = "Select Location"
) => {
  if (!location) return fallback;

  if (location.display_address) return location.display_address;

  if (location.city && location.country) {
    return `${location.city}, ${location.country}`;
  }

  return location.address || fallback;
};

export const getGuestLocationByCountryCode = (countryCode) => {
  const normalizedCode = normalizeCountryCode(countryCode);
  const location = GUEST_LOCATION_PRESETS.find(
    (item) => item.countryCode === normalizedCode
  );

  return cloneLocation(location || DEFAULT_GUEST_LOCATION);
};

export const getGuestLocationByCountryName = (countryName) => {
  const normalizedName = normalizeCountryName(countryName);
  const location = GUEST_LOCATION_PRESETS.find((item) => {
    const itemCountry = normalizeCountryName(item.country);
    const itemCity = normalizeCountryName(item.city);

    return (
      itemCountry === normalizedName ||
      itemCity === normalizedName ||
      normalizedName.includes(itemCountry) ||
      normalizedName.includes(itemCity)
    );
  });

  return cloneLocation(location || DEFAULT_GUEST_LOCATION);
};

export const getMarketMetadata = (location = {}) => {
  const countryCode =
    getCountryCodeFromLocationText(location) ||
    normalizeCountryCode(
      location.countryCode || location.country_code || location.detected_country_code
    ) ||
    getMarketCountryCodeById(getFirstLocationValue(location.country_id, location.countryId)) ||
    inferCountryCodeFromLatLng(location.latitude || location.lat, location.longitude || location.lng) ||
    DEFAULT_GUEST_LOCATION.countryCode;

  return MARKET_METADATA[countryCode] || MARKET_METADATA[DEFAULT_GUEST_LOCATION.countryCode];
};

export const enrichLocationWithMarketMetadata = (location = {}) => {
  const market = getMarketMetadata(location);
  const marketLocation = getGuestLocationByCountryCode(market.countryCode);
  const locationCountryCode = normalizeCountryCode(location.countryCode || location.country_code);
  const locationCountryId = getFirstLocationValue(location.country_id, location.countryId);
  const locationCountryCodeFromId = getMarketCountryCodeById(locationCountryId);
  const normalizedCountryCode =
    locationCountryCode && locationCountryCode !== market.countryCode
      ? market.countryCode
      : locationCountryCode || market.countryCode;
  const normalizedCountryId =
    locationCountryCodeFromId && locationCountryCodeFromId !== market.countryCode
      ? marketLocation.country_id
      : locationCountryId || marketLocation.country_id;

  return {
    ...location,
    country: market.country,
    countryCode: normalizedCountryCode,
    country_code: normalizedCountryCode,
    country_id: normalizedCountryId,
    countryId: normalizedCountryId,
    currency: market.currency,
    currencyLabel: market.currencyLabel,
    marketLabel: market.marketLabel,
  };
};

const getPosition = () =>
  new Promise((resolve, reject) => {
    if (
      typeof navigator === "undefined" ||
      !navigator.geolocation ||
      typeof navigator.geolocation.getCurrentPosition !== "function"
    ) {
      reject(new Error("Geolocation is not supported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    });
  });

const getCountryCodeFromAddressComponents = (addressComponents = []) => {
  const countryComponent = addressComponents.find((component) =>
    component.types?.includes("country")
  );

  return normalizeCountryCode(countryComponent?.short_name);
};

const reverseGeocodeWithGoogleMaps = (lat, lng) =>
  new Promise((resolve) => {
    let settled = false;
    const finish = (countryCode) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve(countryCode || "");
    };
    const timeoutId = setTimeout(
      () => finish(""),
      GEOCODING_TIMEOUT_MS
    );

    if (typeof window === "undefined" || !window.google?.maps?.Geocoder) {
      finish("");
      return;
    }

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== "OK" || !results?.length) {
        finish("");
        return;
      }

      const countryCode = results
        .map((result) =>
          getCountryCodeFromAddressComponents(result.address_components)
        )
        .find(Boolean);

      finish(countryCode);
    });
  });

const reverseGeocodeWithFetch = async (lat, lng) => {
  if (typeof fetch !== "function") return "";

  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), GEOCODING_TIMEOUT_MS)
    : null;

  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`,
      controller ? { signal: controller.signal } : undefined
    );
    const data = await response.json();
    const countryCode = data?.results
      ?.map((result) =>
        getCountryCodeFromAddressComponents(result.address_components)
      )
      .find(Boolean);

    return countryCode || "";
  } catch (error) {
    return "";
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

export const resolveCountryCodeFromLatLng = async (lat, lng) => {
  const googleMapsCountryCode = await reverseGeocodeWithGoogleMaps(lat, lng);

  if (googleMapsCountryCode) return googleMapsCountryCode;

  const fetchedCountryCode = await reverseGeocodeWithFetch(lat, lng);

  return fetchedCountryCode || inferCountryCodeFromLatLng(lat, lng);
};

const geocodeDeliveryLocation = async (query) => {
  if (typeof window !== "undefined" && window.google?.maps?.Geocoder) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error("Location search timed out. Please try again.")), GEOCODING_TIMEOUT_MS);
      new window.google.maps.Geocoder().geocode(query, (results, status) => {
        clearTimeout(timeout);
        if (status === "OK") resolve(results || []);
        else if (status === "ZERO_RESULTS") resolve([]);
        else reject(new Error("Location search is unavailable. Please try again."));
      });
    });
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), GEOCODING_TIMEOUT_MS);
  try {
    const params = new URLSearchParams({ key: GOOGLE_MAPS_API_KEY });
    if (query.address) params.set("address", query.address);
    else params.set("latlng", `${query.location.lat},${query.location.lng}`);
    const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?${params}`, { signal: controller.signal });
    const data = await response.json();
    if (data.status === "ZERO_RESULTS") return [];
    if (!response.ok || data.status !== "OK") throw new Error("Location search is unavailable. Please try again.");
    return data.results || [];
  } finally {
    clearTimeout(timeout);
  }
};

const normalizeDeliveryResult = (result) => {
  const coordinates = result?.geometry?.location;
  const latitude = typeof coordinates?.lat === "function" ? coordinates.lat() : coordinates?.lat;
  const longitude = typeof coordinates?.lng === "function" ? coordinates.lng() : coordinates?.lng;
  const countryCode = getCountryCodeFromAddressComponents(result?.address_components || []);
  const location = {
    latitude,
    longitude,
    address: result?.formatted_address,
    countryCode: countryCode || inferCountryCodeFromLatLng(latitude, longitude),
    place_id: result?.place_id,
    selected_from_address_list: false,
    delivery_location_selected: true,
    is_guest_default_location: false,
  };
  return hasSelectedDeliveryLocation(location) ? enrichLocationWithMarketMetadata(location) : null;
};

export const searchDeliveryLocations = async (query) => {
  const results = await geocodeDeliveryLocation({ address: query.trim() });
  return results.map(normalizeDeliveryResult).filter(Boolean).slice(0, 5);
};

export const detectDeliveryLocation = async () => {
  const position = await getPosition();
  const latitude = position.coords.latitude;
  const longitude = position.coords.longitude;
  const results = await geocodeDeliveryLocation({ location: { lat: latitude, lng: longitude } });
  const location = results.map(normalizeDeliveryResult).find(Boolean);
  if (!location) throw new Error("We couldn't find your address. Please enter your area below.");
  return { ...location, latitude, longitude, location_permission_status: "granted" };
};

export const getGuestLocationFromBrowser = async () => {
  try {
    const position = await getPosition();
    const detectedLatitude = position.coords.latitude;
    const detectedLongitude = position.coords.longitude;
    const detectedCountryCode = await resolveCountryCodeFromLatLng(
      detectedLatitude,
      detectedLongitude
    );
    const selectedLocation = getGuestLocationByCountryCode(detectedCountryCode);

    return {
      location: {
        ...selectedLocation,
        detected_country_code: detectedCountryCode,
        location_permission_status: "granted",
      },
      permissionDenied: false,
      shouldRedirectToAddressList: false,
    };
  } catch (error) {
    return {
      location: {
        ...cloneLocation(DEFAULT_GUEST_LOCATION),
        location_permission_status: "denied",
      },
      permissionDenied: true,
      shouldRedirectToAddressList: true,
      error,
    };
  }
};
